// «El mensaje más nuevo es una respuesta» — a sidebar row whose newest message is a thread reply
// ASKS which door to open (ios/Sources/Views/ThreadOrChannelChoice.swift, Android
// ThreadOrChannelSheet, rule for rule). Each door shows its own last message and when it landed:
// "hilo" and "conversación" name the mechanism; the reader knows what was said, not where it was
// nested. The thread is listed FIRST — it is the message the reader just read in the list. Desktop:
// a popover anchored to the row (the Mac's); phone width: a bottom sheet (iOS/Android).
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import type { DisplayChannel } from '../../../core/channelListDisplayModels';
import { messagePreview, previewSource } from '../../../core/channelListDerivation';
import { RelativeTime } from '../../../core/relativeTime';
import type { ChannelRootPreview } from '../../../data/features/threads';
import { t } from '../../../i18n/i18n';
import { ShellContext } from '../../shell/ShellContext';
import './threads.css';

export interface ThreadChoice {
  row: DisplayChannel;
  anchor: DOMRect | null;
}

/** Whether a click on this row asks instead of opening (`previewThreadRootId` is set only for replies). */
export function asksThreadOrChannel(row: DisplayChannel | undefined, isSearching: boolean): boolean {
  return !!row?.previewThreadRootId && !isSearching;
}

export function ThreadOrChannelChooser({ choice, onClose, onOpenChannel }: { choice: ThreadChoice; onClose(): void; onOpenChannel(channelId: string): void }) {
  const shell = useContext(ShellContext);
  const ref = useRef<HTMLDivElement>(null);
  const [channelSide, setChannelSide] = useState<ChannelRootPreview | null | undefined>(undefined);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;
  const { row } = choice;

  useEffect(() => {
    let alive = true;
    void data.call('channelRootPreview', row.id).then((p) => alive && setChannelSide(p));
    return () => {
      alive = false;
    };
  }, [row.id]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || phone) return;
    const r = el.getBoundingClientRect();
    const a = choice.anchor;
    const left = a ? a.right + 8 : 80;
    const top = a ? a.top : 80;
    setPos({ left: Math.max(8, Math.min(left, window.innerWidth - r.width - 8)), top: Math.max(8, Math.min(top, window.innerHeight - r.height - 8)) });
  }, [choice.anchor, phone, channelSide]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const outside = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const arm = setTimeout(() => window.addEventListener('mousedown', outside));
    window.addEventListener('keydown', esc);
    return () => {
      clearTimeout(arm);
      window.removeEventListener('mousedown', outside);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
  }, []);

  const now = Date.now();
  const labels = { yesterday: t('channelList.time.yesterday'), today: t('channelList.time.today') };
  const channelPreview = channelSide
    ? messagePreview({
        message: previewSource(channelSide.message, channelSide.props),
        authorName: channelSide.authorName,
        isOwnMessage: channelSide.userId === shell?.me.id,
        showsAuthorPrefix: channelSide.channelType !== 'D',
        youLabel: t('channelList.preview.you'),
        attachmentPlaceholder: t('channelList.preview.attachment'),
      })
    : '';

  const openThread = () => {
    onClose();
    if (row.previewThreadRootId) shell?.openThreadInConversation(row.id, row.previewThreadRootId);
  };
  const openChannel = () => {
    onClose();
    onOpenChannel(row.id);
  };

  const body = (
    <div
      ref={ref}
      className={`popover thread-choice${phone ? ' sheet' : ''}`}
      role="dialog"
      aria-label={t('channelList.threadChoice.title')}
      data-thread-choice={row.id}
      style={phone ? undefined : pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
    >
      <h3>{t('channelList.threadChoice.title')}</h3>
      <p className="lede">{t('channelList.threadChoice.body')}</p>
      <ChoiceRow
        glyph="↳"
        label={t('channelList.threadChoice.openThread')}
        preview={row.preview ?? ''}
        stamp={RelativeTime.formatWithTime(row.previewAt, now, labels)}
        onClick={openThread}
        kind="thread"
      />
      <ChoiceRow
        glyph="#"
        label={t('channelList.threadChoice.openChannel')}
        // A channel whose root posts all aged out of the cache genuinely has nothing to show here.
        preview={channelSide === undefined ? '' : channelPreview || t('channelList.threadChoice.channelEmpty')}
        stamp={channelSide ? RelativeTime.formatWithTime(channelSide.createAt, now, labels) : ''}
        onClick={openChannel}
        kind="channel"
      />
    </div>
  );
  return createPortal(phone ? <div className="scrim thread-choice-scrim">{body}</div> : body, document.body);
}

function ChoiceRow({ glyph, label, preview, stamp, onClick, kind }: { glyph: string; label: string; preview: string; stamp: string; onClick(): void; kind: string }) {
  return (
    <button type="button" className="thread-choice-row" onClick={onClick} data-choice={kind}>
      <span className="glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="text">
        <span className="label">{label}</span>
        <span className="preview">{preview}</span>
      </span>
      <span className="stamp">{stamp}</span>
    </button>
  );
}
